import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import SearchFiltersPanel from '../SearchFiltersPanel/SearchFiltersPanel';
import BookingDateRangeFilter from '../BookingDateRangeFilter/BookingDateRangeFilter';
import LocationAutocompleteInput from '../../../components/LocationAutocompleteInput/LocationAutocompleteInput';
import FilterComponent from '../FilterComponent';
import { IconLocation } from '../../../components';
import GuestPicker from '../GuestPicker/GuestPicker';
import css from './SearchFilterBar.module.css';

const CategoryIcon = () => (
  <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>⚓</span>
);

const MapIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: '#0369a1' }}>
    <path d="M8 1.5C5.515 1.5 3.5 3.515 3.5 6c0 3.5 4.5 9 4.5 9s4.5-5.5 4.5-9c0-2.485-2.015-4.5-4.5-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <circle cx="8" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

// Each tab filters by pub_categoryLevel1. categoryIds: null means "All" (clear filter).
const TABS = [
  { label: 'Explore All',       categoryIds: null },
  { label: 'Boat Rentals',     categoryIds: ['BoatRentals'] },
  { label: 'Private Charters', categoryIds: ['private_charters'] },
  { label: 'Experiences',      categoryIds: ['ticketed_charters'] },
  { label: 'Captains',         categoryIds: ['captains'] },
  { label: 'Services',         categoryIds: ['dock-ramp-delivery', 'moorage-storage-parking', 'mechanics-and-technicians'] },
  { label: 'Paddle & Gear',    categoryIds: ['accessories-boating'] },
];

const parseHasAny = raw => {
  if (!raw) return [];
  const s = raw.toString();
  return s.startsWith('has_any:')
    ? s.slice('has_any:'.length).split(',').map(v => v.trim()).filter(Boolean)
    : [s];
};

// Airbnb-style sliders icon
const FiltersIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <line x1="2" y1="4"  x2="14" y2="4"  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="2" y1="8"  x2="14" y2="8"  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="2" y1="12" x2="14" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="5"  cy="4"  r="2" fill="white" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="11" cy="8"  r="2" fill="white" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="7"  cy="12" r="2" fill="white" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

// Format "2024-06-01,2024-06-03" → "Jun 1 – 3" (or "Jun 1 – Jul 3" if different months)
const formatDateRange = dates => {
  if (!dates) return null;
  const [start, end] = dates.split(',');
  if (!start) return null;
  try {
    const s = new Date(start + 'T12:00:00');
    const e = end ? new Date(end + 'T12:00:00') : null;
    const startStr = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!e) return startStr;
    const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
    const endStr = e.toLocaleDateString('en-US', sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' });
    return `${startStr} – ${endStr}`;
  } catch {
    return null;
  }
};

const SearchFilterBar = ({
  validQueryParams,
  onSubmit,
  defaultFiltersConfig,
  marketplaceCurrency,
  initialValuesFn,
  getHandleChangedValueFn,
  listingCategories,
  intl,
  totalItems,
  searchInProgress,
  onManageDisableScrolling,
  className,
  // Current search address (from URL address param) — shown in mobile pill
  address,
  // Map toggle — displayed in the pinned right area of the tab row
  isMapVisible,
  onToggleMap,
  // Mobile location search — bypasses getHandleChangedValueFn which strips address/bounds
  onLocationSearch,
  // Mobile-specific map opener/closer
  onMobileMapOpen,
  onMobileMapClose,
  isMapOpen,
  // Sort — passed through to the filter panel as raw data
  isSortActive,
  sortOptions,
  sortQueryParamName,
  currentSort,
  onSortSelect,
  // External panel open control — set to true from SearchPageTopbar pill click
  externalOpen,
  onExternalOpenHandled,
  // Null-stripping search handler for guest picker (same as SearchPageTopbar's onSearch)
  onGuestSearch,
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isCategorySheetOpen, setIsCategorySheetOpen] = useState(false);
  const [isDateSheetOpen, setIsDateSheetOpen] = useState(false);
  const [isLocationSheetOpen, setIsLocationSheetOpen] = useState(false);
  const [isPriceSheetOpen, setIsPriceSheetOpen] = useState(false);

  // Location input state — mirrors SearchPageTopbar
  const [locationValue, setLocationValue] = useState({
    search: address || '',
    predictions: [],
    selectedPlace: null,
  });
  useEffect(() => {
    setLocationValue(prev => ({ ...prev, search: address || '', selectedPlace: null }));
  }, [address]);

  // Open the panel when triggered externally (e.g. from SearchPageTopbar pill click)
  useEffect(() => {
    if (externalOpen) {
      setIsPanelOpen(true);
      onExternalOpenHandled?.();
    }
  }, [externalOpen]);

  // ── Parsed active filter values
  const activeCats = parseHasAny(validQueryParams?.pub_categoryLevel1);

  const getIsTabActive = tab => {
    if (tab.categoryIds === null) return activeCats.length === 0;
    return tab.categoryIds.some(id => activeCats.includes(id));
  };

  const handleTabClick = tab => {
    if (tab.categoryIds === null) {
      onSubmit({ pub_categoryLevel1: null, pub_listingType: null });
      return;
    }
    const isActive = tab.categoryIds.some(id => activeCats.includes(id));
    const nextCats = isActive
      ? activeCats.filter(id => !tab.categoryIds.includes(id))
      : [...new Set([...activeCats, ...tab.categoryIds])];
    const catParam = nextCats.length === 0 ? null :
      nextCats.length === 1 ? nextCats[0] :
      `has_any:${nextCats.join(',')}`;
    onSubmit({ pub_categoryLevel1: catParam, pub_listingType: null });
  };

  // ── Active filter state
  const hasDates = !!validQueryParams?.dates;
  const hasPrice = !!validQueryParams?.price;
  const hasSeats = !!validQueryParams?.seats;
  const hasPanelFilters = hasDates || hasPrice || hasSeats;

  const activeTabs = TABS.slice(1).filter(tab => getIsTabActive(tab));

  // Configs passed through to the panel
  const datesConfig = defaultFiltersConfig?.find(f => f.schemaType === 'dates');
  const priceConfig = defaultFiltersConfig?.find(f => f.schemaType === 'price');
  const seatsConfig = defaultFiltersConfig?.find(f => f.key === 'seats');

  // Label for the mobile Category button — shows active tab name(s) or "All"
  const mobileCategoryLabel =
    activeTabs.length === 1 ? activeTabs[0].label :
    activeTabs.length > 1  ? `${activeTabs.length} selected` :
    'Explore All';
  const isCategoryActive = activeCats.length > 0;
  // Count active panel filters for mobile badge (price, dates, seats, sort)
  const hasSort = !!(validQueryParams?.[sortQueryParamName] && validQueryParams[sortQueryParamName] !== 'createdAt');
  const panelFilterCount = [hasDates, hasPrice, hasSeats, hasSort].filter(Boolean).length;

  const formattedDates = formatDateRange(validQueryParams?.dates);

  return (
    <div className={classNames(css.root, className)}>
      {/* Mobile search pill — hidden on desktop */}
      <div className={css.mobileSearchPill}>
        <button
          type="button"
          className={css.pillLocationBtn}
          onClick={() => setIsLocationSheetOpen(true)}
          aria-label="Edit location"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: '#6B7280' }}>
            <path d="M8 1.5C5.515 1.5 3.5 3.515 3.5 6c0 3.5 4.5 9 4.5 9s4.5-5.5 4.5-9c0-2.485-2.015-4.5-4.5-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
            <circle cx="8" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
          </svg>
          <span className={css.pillLocationText}>{address || 'Where'}</span>
        </button>
        <span className={css.pillDivider} aria-hidden="true" />
        <button
          type="button"
          className={css.pillDatesBtn}
          onClick={() => setIsDateSheetOpen(true)}
          aria-label={formattedDates ? 'Edit dates' : 'Add dates'}
        >
          {formattedDates || 'When'}
        </button>
        {onGuestSearch && (
          <>
            <span className={css.pillDivider} aria-hidden="true" />
            <GuestPicker
              onSearch={onGuestSearch}
              triggerClassName={css.pillGuestBtn}
              triggerActiveClassName={css.pillGuestBtnActive}
            />
          </>
        )}
      </div>

      {/* Mobile 3-button control row — hidden on desktop */}
      <div className={css.mobileControlRow}>
        <button
          type="button"
          className={classNames(css.mobileControlBtn, { [css.mobileControlBtnActive]: isCategoryActive })}
          onClick={() => setIsCategorySheetOpen(true)}
        >
          <CategoryIcon />
          {mobileCategoryLabel}
        </button>
        <button
          type="button"
          className={classNames(css.mobileControlBtn, { [css.mobileControlBtnActive]: isPriceSheetOpen || hasPrice })}
          onClick={() => setIsPriceSheetOpen(true)}
        >
          Price Range
        </button>
        {(onMobileMapOpen || onToggleMap) && (
          <button
            type="button"
            className={classNames(css.mobileControlBtn, { [css.mobileControlBtnActive]: isMapOpen })}
            onClick={isMapOpen ? onMobileMapClose : (onMobileMapOpen || onToggleMap)}
          >
            <MapIcon />
            {isMapOpen ? '✕ Map' : 'Map'}
          </button>
        )}
      </div>

      {/* Tab row — hidden on mobile via CSS */}
      <div className={css.tabRow}>
        {/* Scrollable area: tabs + Filters pill */}
        <div className={css.tabScroll}>
          {TABS.map(tab => {
            const isActive = getIsTabActive(tab);
            return (
              <button
                key={tab.label}
                type="button"
                className={classNames(css.tab, { [css.tabActive]: isActive })}
                onClick={() => handleTabClick(tab)}
              >
                {tab.label}
                {tab.categoryIds !== null && (
                  <span aria-hidden="true" style={{ marginLeft: 5, opacity: isActive ? 0.6 : 0, fontSize: '0.85em' }}>×</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Pinned right area: Filters pill + map toggle */}
        <div className={css.tabRowEnd}>
          <button
            type="button"
            className={classNames(css.filtersPill, {
              [css.filtersPillActive]: isPriceSheetOpen || hasPrice,
            })}
            onClick={() => setIsPriceSheetOpen(v => !v)}
          >
            Price Range
          </button>
          {onToggleMap && (
            <button
              type="button"
              className={classNames(css.mapToggleBtn, {
                [css.mapToggleBtnActive]: isMapVisible,
              })}
              onClick={onToggleMap}
            >
              {isMapVisible ? 'Map Open' : 'Open Map'}
            </button>
          )}
        </div>
      </div>

      {/* Centered modal filter panel */}
      <SearchFiltersPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        validQueryParams={validQueryParams}
        onSubmit={onSubmit}
        listingCategories={listingCategories}
        totalItems={totalItems}
        searchInProgress={searchInProgress}
        datesConfig={datesConfig}
        priceConfig={priceConfig}
        seatsConfig={seatsConfig}
        marketplaceCurrency={marketplaceCurrency}
        initialValuesFn={initialValuesFn}
        getHandleChangedValueFn={getHandleChangedValueFn}
        intl={intl}
        isSortActive={isSortActive}
        sortOptions={sortOptions}
        sortQueryParamName={sortQueryParamName}
        currentSort={currentSort}
        onSortSelect={onSortSelect}
      />

      {/* Mobile date picker bottom sheet */}
      {isDateSheetOpen && (
        <>
          <div className={css.sheetBackdrop} onClick={() => setIsDateSheetOpen(false)} />
          <div className={css.dateSheet} role="dialog" aria-label="Select dates">
            <div className={css.sheetHandle} />
            <div className={css.sheetTitleRow}>
              <div className={css.sheetTitle}>Select dates</div>
              <button
                type="button"
                className={css.sheetCloseBtn}
                onClick={() => setIsDateSheetOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className={css.dateSheetContent}>
              <BookingDateRangeFilter
                id="mobile-dates"
                queryParamNames={['dates']}
                initialValues={validQueryParams || {}}
                showAsPopup={false}
                liveEdit
                onSubmit={params => {
                  onSubmit(params);
                  if (params.dates) setIsDateSheetOpen(false);
                }}
                intl={intl}
              />
            </div>
          </div>
        </>
      )}

      {/* Mobile location search bottom sheet */}
      {isLocationSheetOpen && (
        <>
          <div className={css.sheetBackdrop} onClick={() => setIsLocationSheetOpen(false)} />
          <div className={css.locationSheet} role="dialog" aria-label="Search location">
            <div className={css.sheetHandle} />
            <div className={css.sheetTitleRow}>
              <div className={css.sheetTitle}>Location</div>
              <button
                type="button"
                className={css.sheetCloseBtn}
                onClick={() => setIsLocationSheetOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className={css.locationSheetContent}>
              <LocationAutocompleteInput
                id="mobile-location"
                rootClassName={css.locationSheetInputRoot}
                inputClassName={css.locationSheetInput}
                predictionsClassName={css.locationSheetPredictions}
                iconClassName={css.locationSheetIcon}
                CustomIcon={() => <IconLocation rootClassName={css.locationSheetIconSvg} />}
                placeholder="City, lake, or region"
                useDarkText={true}
                disableKeywordSuggestions={true}
                geocoderTypes={['place', 'region', 'country']}
                closeOnBlur={false}
                hideErrorMessage
                autoFocus
                input={{
                  name: 'mobile-location',
                  value: locationValue,
                  onChange: value => {
                    setLocationValue(value);
                    if (value?.selectedPlace) {
                      const { search } = value;
                      const { origin, bounds } = value.selectedPlace;
                      if (onLocationSearch) {
                        onLocationSearch({
                          address: search,
                          bounds,
                          ...(origin ? { origin: `${origin.lat},${origin.lng}` } : {}),
                        });
                      }
                      setIsLocationSheetOpen(false);
                    }
                  },
                  onBlur: () => {},
                  onFocus: () => {},
                }}
                meta={{ valid: true, invalid: false, touched: false, error: undefined }}
              />
            </div>
          </div>
        </>
      )}

      {/* Price range sheet — mobile bottom sheet, desktop dropdown */}
      {isPriceSheetOpen && (
        <>
          <div className={css.sheetBackdrop} onClick={() => setIsPriceSheetOpen(false)} />
          <div className={css.priceSheet} role="dialog" aria-label="Price range">
            <div className={css.sheetHandle} />
            <div className={css.sheetTitleRow}>
              <div className={css.sheetTitle}>Price Range</div>
              <button
                type="button"
                className={css.sheetCloseBtn}
                onClick={() => setIsPriceSheetOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className={css.priceSheetContent}>
              {priceConfig ? (
                <FilterComponent
                  key="price-sheet"
                  id="PriceSheet.price"
                  config={priceConfig}
                  containerId="PriceSheet"
                  listingCategories={listingCategories}
                  marketplaceCurrency={marketplaceCurrency}
                  urlQueryParams={validQueryParams}
                  initialValues={initialValuesFn}
                  getHandleChangedValueFn={getHandleChangedValueFn}
                  intl={intl}
                  showAsPopup={false}
                  liveEdit={true}
                  initialIsOpen={true}
                />
              ) : (
                <p style={{ padding: '16px', color: '#6B7280', fontSize: '14px' }}>
                  Price filter not available.
                </p>
              )}
            </div>
            <div className={css.priceSheetFooter}>
              <button
                type="button"
                className={css.priceSheetClear}
                onClick={() => {
                  onSubmit({ price: null });
                  setIsPriceSheetOpen(false);
                }}
              >
                Clear
              </button>
              <button
                type="button"
                className={css.priceSheetDone}
                onClick={() => setIsPriceSheetOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}

      {/* Mobile category bottom sheet */}
      {isCategorySheetOpen && (
        <>
          <div
            className={css.sheetBackdrop}
            onClick={() => setIsCategorySheetOpen(false)}
          />
          <div className={css.categorySheet} role="dialog" aria-label="Category">
            <div className={css.sheetHandle} />
            <div className={css.sheetTitleRow}>
              <div className={css.sheetTitle}>Category</div>
              <button
                type="button"
                className={css.sheetCloseBtn}
                onClick={() => setIsCategorySheetOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className={css.sheetTabList}>
              {TABS.map(tab => {
                const isActive = getIsTabActive(tab);
                return (
                  <button
                    key={tab.label}
                    type="button"
                    className={classNames(css.sheetTabBtn, {
                      [css.sheetTabBtnActive]: isActive,
                    })}
                    onClick={() => {
                      handleTabClick(tab);
                      if (tab.categoryIds === null) setIsCategorySheetOpen(false);
                    }}
                  >
                    {tab.label}
                    {isActive && tab.categoryIds !== null && (
                      <span style={{ marginLeft: 6, opacity: 0.6, fontSize: '0.85em' }}>×</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SearchFilterBar;
